import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare,
  Star,
  AlertCircle,
  TrendingUp,
  Maximize2,
  Save,
  CheckCircle2,
  Lightbulb,
} from 'lucide-react';
import type { Employee, ReviewData } from '../index';

interface NarrativePromptsPanelProps {
  employee: Employee;
  reviewData: ReviewData;
  onPromptsUpdate: (prompts: Record<string, string>) => void;
}

interface PromptConfig {
  id: string;
  questionKey: string;
  Icon: typeof Star;
  color: string;
  placeholderKey: string;
}

const NarrativePromptsPanel = ({
  reviewData,
  onPromptsUpdate,
}: NarrativePromptsPanelProps) => {
  const { t } = useTranslation();

  const [responses, setResponses] = useState<Record<string, string>>(() => {
    try {
      if (reviewData.snapshot?.narrativePromptsSummary) {
        return JSON.parse(reviewData.snapshot.narrativePromptsSummary);
      }
    } catch { /* ignore parse errors */ }
    return {
      impactMatters: '',
      limitedImpact: '',
      doMoreLess: '',
      scopeExpansion: '',
    };
  });

  const [showSaveToast, setShowSaveToast] = useState(false);

  const prompts: PromptConfig[] = [
    {
      id: 'impactMatters',
      questionKey: 'performanceSnapshot.prompts.impactMatters.question',
      Icon: Star,
      color: 'from-yellow-500 to-orange-500',
      placeholderKey: 'performanceSnapshot.prompts.impactMatters.placeholder',
    },
    {
      id: 'limitedImpact',
      questionKey: 'performanceSnapshot.prompts.limitedImpact.question',
      Icon: AlertCircle,
      color: 'from-red-500 to-pink-500',
      placeholderKey: 'performanceSnapshot.prompts.limitedImpact.placeholder',
    },
    {
      id: 'doMoreLess',
      questionKey: 'performanceSnapshot.prompts.doMoreLess.question',
      Icon: TrendingUp,
      color: 'from-blue-500 to-cyan-500',
      placeholderKey: 'performanceSnapshot.prompts.doMoreLess.placeholder',
    },
    {
      id: 'scopeExpansion',
      questionKey: 'performanceSnapshot.prompts.scopeExpansion.question',
      Icon: Maximize2,
      color: 'from-purple-500 to-indigo-500',
      placeholderKey: 'performanceSnapshot.prompts.scopeExpansion.placeholder',
    },
  ];

  const handleResponseChange = (promptId: string, value: string) => {
    const updatedResponses = {
      ...responses,
      [promptId]: value,
    };
    setResponses(updatedResponses);
    onPromptsUpdate(updatedResponses);
  };

  const handleSaveAll = () => {
    onPromptsUpdate(responses);
    setShowSaveToast(true);
    setTimeout(() => setShowSaveToast(false), 3000);
  };

  const completionCount = Object.values(responses).filter(
    (r) => r.trim().length > 0,
  ).length;
  const completionPercentage = (completionCount / prompts.length) * 100;

  return (
    <div className="space-y-6">
      {/* Header with Progress */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold text-foreground flex items-center space-x-2">
              <MessageSquare size={24} />
              <span>{t('performanceSnapshot.prompts.title')}</span>
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {t('performanceSnapshot.prompts.subtitle')}
            </p>
          </div>
          <button
            onClick={handleSaveAll}
            disabled={completionCount === 0}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={16} />
            <span>{t('performanceSnapshot.prompts.saveResponses')}</span>
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">
              {t('performanceSnapshot.prompts.completion')}: {completionCount}{' '}
              {t('performanceSnapshot.prompts.of')} {prompts.length}
            </span>
            <span className="text-sm text-muted-foreground">
              {Math.round(completionPercentage)}%
            </span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 transition-all duration-300"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Prompt Cards */}
      <div className="space-y-6">
        {prompts.map((prompt) => (
          <div key={prompt.id} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-start space-x-4 mb-4">
              <div
                className={`w-12 h-12 bg-gradient-to-br ${prompt.color} rounded-lg flex items-center justify-center flex-shrink-0`}
              >
                <prompt.Icon size={24} color="white" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  {t(prompt.questionKey)}
                </h4>
                <textarea
                  value={responses[prompt.id] || ''}
                  onChange={(e) => handleResponseChange(prompt.id, e.target.value)}
                  placeholder={t(prompt.placeholderKey)}
                  rows={6}
                  className="w-full p-4 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none text-sm"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-muted-foreground">
                    {responses[prompt.id]?.length || 0}{' '}
                    {t('performanceSnapshot.prompts.characters')}
                  </span>
                  {responses[prompt.id]?.trim().length > 0 && (
                    <div className="flex items-center space-x-1 text-green-600">
                      <CheckCircle2 size={14} />
                      <span className="text-xs font-medium">
                        {t('performanceSnapshot.prompts.completed')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tips Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Lightbulb size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-foreground mb-2">
              {t('performanceSnapshot.prompts.tips.title')}
            </h4>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>{t('performanceSnapshot.prompts.tips.tip1')}</li>
              <li>{t('performanceSnapshot.prompts.tips.tip2')}</li>
              <li>{t('performanceSnapshot.prompts.tips.tip3')}</li>
              <li>{t('performanceSnapshot.prompts.tips.tip4')}</li>
              <li>{t('performanceSnapshot.prompts.tips.tip5')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Save Toast */}
      {showSaveToast && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2 z-50">
          <CheckCircle2 size={18} />
          <span className="text-sm font-medium">
            {t('performanceSnapshot.prompts.savedSuccess')}
          </span>
        </div>
      )}
    </div>
  );
};

export default NarrativePromptsPanel;
